import { useEffect, useRef, useState } from 'react'
import type { CompressionType } from '@/api/uploadFile'
import { useFileUpload } from './useFileUpload'
import { useUploadQueue } from './UploadQueueContext'
import { placeKey, type BlockUploadTarget } from './uploadTask'
import { setPlaceProgress, usePlaceProgress } from './imageUploadProgress'

interface Input {
  value: string | null
  onChange: (url: string | null) => void | Promise<boolean>
  compressionType: CompressionType
  disabled: boolean
  blockTarget?: BlockUploadTarget
}

/**
 * Загрузка/снятие картинки одного места — общая для `ImageUpload` (панель) и
 * картинки на холсте составного блока (`CanvasImageFilled`): у обоих одна очередь,
 * одна цель догрузки и одно правило удаления старого блоба.
 */
export const useImageDrop = ({
  value,
  onChange,
  compressionType,
  disabled,
  blockTarget,
}: Input) => {
  const { removeFile } = useFileUpload({ compressionType })
  const { enqueue } = useUploadQueue()
  // Задача переживает компонент, поэтому в неё уходят РЕФЫ, а не замыкания:
  // `onChange` вызывается ПОСЛЕДНИЙ (напечатанное во время загрузки не
  // откатывается), а место сверяется по идентичности, а не по монтированию —
  // редактор блока рендерится без `key`, и на соседнем блоке того же типа этот
  // же экземпляр уже обслуживает ЧУЖОЙ черновик.
  const mountedRef = useRef(true)
  const onChangeRef = useRef(onChange)
  const targetRef = useRef(blockTarget)
  useEffect(() => {
    onChangeRef.current = onChange
    targetRef.current = blockTarget
  })
  useEffect(() => {
    // Ставится и на монтировании: под StrictMode (и при офскрин-переиспользовании)
    // эффекты монтируются повторно, и без этой строки место навсегда осталось бы
    // «мёртвым» — весь блочный путь ушёл бы в фоновый PUT под открытой панелью.
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // У блочного места прогресс — по ключу места (`imageUploadProgress`): этот же
  // экземпляр на соседнем блоке показал бы чужую полосу. Место без блока
  // (курс, категория, статья) одно на компонент — там хватает состояния.
  const [localProgress, setLocalProgress] = useState<number | null>(null)
  const placeProgress = usePlaceProgress(blockTarget ? placeKey(blockTarget) : null)
  const progress = blockTarget ? placeProgress : localProgress

  // Задача уходит в очередь целиком: загрузка больше не умирает вместе с
  // панелью. Обещание «старый блоб удаляем только после подтверждённого
  // сохранения» переехало в `runUploadTask` — оно общее для обоих путей.
  const onDrop = (files: File[]) => {
    if (disabled) return
    const file = files[0]
    if (!file) return
    const placed = blockTarget
    const placedKey = placed ? placeKey(placed) : null
    enqueue({
      file,
      compressionType,
      previousUrl: value,
      commit: (url) => onChangeRef.current(url),
      block: blockTarget,
      // Ключ места считается В МОМЕНТ коммита тем `keyOf`, что дал вызывающий:
      // у брошенной цели и у текущей — одной функцией.
      isAlive: (keyOf = placeKey) => {
        const current = targetRef.current
        const key = (t: BlockUploadTarget | undefined) => (t ? keyOf(t) : null)
        return mountedRef.current && key(current) === key(placed)
      },
      onProgress: (percent) =>
        placedKey === null ? setLocalProgress(percent) : setPlaceProgress(placedKey, percent),
    })
  }

  const handleRemove = async () => {
    if (disabled || !value) return
    const oldUrl = value
    const saved = await onChange(null)
    // У БЛОЧНОГО места `onChange` пишет в ЧЕРНОВИК и возвращает `void`:
    // подтверждённого сохранения не было, серверный блок ссылается на файл до
    // нажатия «Сохранить» — то же правило, по которому путь ЗАМЕНЫ возвращает
    // `false` (`UploadQueueProvider.resolveCommit`). С undo/redo (п.11) это уже
    // не невезение, а штатный путь: снять картинку → «Отменить» → «Сохранить»
    // вернуло бы блоку ссылку на удалённый файл.
    if (blockTarget) return
    if (saved !== false) removeFile(oldUrl)
  }

  return { onDrop, handleRemove, progress }
}
